import { TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import { Box, ListItemButton, Stack, Typography } from '@mui/material';
import { setTransportConditionSelectedItem } from 'app/actions/map';
import { Autosizer, Scrollbar } from 'app/components';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { VariableSizeList } from 'react-window';
function getLastItem(items) {
    return items.length > 0 ? items[items.length - 1] : undefined;
}
function estimateMessageLength(message, item) {
    var _a, _b, _c, _d, _e, _f;
    switch (message.name) {
        case 'geofence_id':
        case 'ibutton1':
            return ((_b = (_a = message.body) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) + String((_c = message.value) !== null && _c !== void 0 ? _c : '').length;
        case 'command': {
            let name = '';
            switch (item.data.sender_type) {
                case 1:
                    name = 'Система';
                    break;
                case 2:
                    name = 'Пользователь';
                    break;
                case 3:
                    name = 'Токен';
                    break;
            }
            return (name.length +
                (item.data.sender ? `«${item.data.sender}»` : '').length +
                'отправил(-а) команду'.length +
                `«${(_d = message.body) !== null && _d !== void 0 ? _d : ''}»`.length);
        }
        default:
            return (_f = (_e = message.body) === null || _e === void 0 ? void 0 : _e.length) !== null && _f !== void 0 ? _f : 0;
    }
}
function formatEventMessage(message, item) {
    switch (message.name) {
        case 'geofence_id':
        case 'ibutton1':
        case 'bt_id_1':
            return `${message.body} «${message.value}»`;
        case 'command':
            switch (item.data.sender_type) {
                case 1:
                    return `Система отправила команду «${message.body}»`;
                case 2:
                    return `Пользователь «${item.data.sender}» отправил(-а) команду «${message.body}»`;
                case 3:
                    return `Токен «${item.data.sender}» отправил(-а) команду «${message.body}»`;
            }
            return undefined;
        default:
            return message.body;
    }
}
function parseStyleOffset(value) {
    if (typeof value === 'number') {
        return value;
    }
    return parseFloat(String(value !== null && value !== void 0 ? value : 0));
}
const MapTransportEventList = ({ renderEmpty }) => {
    const dispatch = useDispatch();
    const listRef = useRef(null);
    const conditionItems = useSelector((state) => { var _a, _b; return (_b = (_a = state.map.transportSelectedItem) === null || _a === void 0 ? void 0 : _a.conditions) !== null && _b !== void 0 ? _b : []; });
    const checkedItems = useSelector((state) => state.map.transportConditionFilterCheckedItems);
    const conditionSelectedItem = useSelector((state) => state.map.transportConditionSelectedItem);
    const { ref: mapRef, isHistoryOpen, historySelectedTime } = useSelector((state) => state.map);
    const items = useMemo(() => {
        return conditionItems.reduce((accumulator, currentValue) => {
            var _a;
            const messages = ((_a = currentValue.messages) !== null && _a !== void 0 ? _a : []).filter((message) => checkedItems.indexOf(message.key) !== -1);
            const currentElement = Object.assign(Object.assign({}, currentValue), { messages });
            if (currentElement.messages.length !== 0) {
                return [...accumulator, currentElement];
            }
            return accumulator;
        }, []);
    }, [conditionItems, checkedItems]);
    const itemHeights = useMemo(() => {
        return items.map((item) => {
            return (Math.ceil(item.messages.reduce((accumulator, currentValue) => accumulator + estimateMessageLength(currentValue, item), 0) /
                config.mapEventListOptions.itemLetters) *
                16 +
                config.mapEventListOptions.itemHeight);
        });
    }, [items]);
    const getItemSize = (index) => {
        return itemHeights[index];
    };
    const itemSelectedIndex = useMemo(() => {
        if (!conditionSelectedItem) {
            return -1;
        }
        if (!isHistoryOpen) {
            return items.findIndex((item) => item.datetime === conditionSelectedItem.datetime);
        }
        const matched = getLastItem(items.filter((item) => moment(item.datetime).isBefore(historySelectedTime)));
        return matched ? items.findIndex((item) => item.datetime === matched.datetime) : -1;
    }, [conditionSelectedItem, isHistoryOpen, items, historySelectedTime]);
    const itemHeightsSum = useMemo(() => {
        return itemHeights.slice(0, itemSelectedIndex).reduce((a, b) => a + b, 0);
    }, [itemSelectedIndex, itemHeights]);
    const handleItemClick = (value) => {
        var _a, _b, _c, _d;
        dispatch(setTransportConditionSelectedItem(value));
        const { lat, lon } = value.data;
        if (lat == null || lon == null) {
            return;
        }
        (_b = (_a = mapRef === null || mapRef === void 0 ? void 0 : mapRef.current) === null || _a === void 0 ? void 0 : _a.handleClosePopup) === null || _b === void 0 ? void 0 : _b.call(_a);
        (_d = (_c = mapRef === null || mapRef === void 0 ? void 0 : mapRef.current) === null || _c === void 0 ? void 0 : _c.handleFlyTo) === null || _d === void 0 ? void 0 : _d.call(_c, [lat, lon]);
    };
    useEffect(() => {
        if (listRef.current && items.length > 0) {
            listRef.current.scrollToItem(itemSelectedIndex !== -1 ? itemSelectedIndex : items.length - 1, 'end');
        }
    }, [checkedItems, itemSelectedIndex, items.length]);
    useEffect(() => {
        if (listRef.current && itemSelectedIndex === -1 && items.length > 0) {
            listRef.current.scrollToItem(items.length - 1, 'end');
        }
    }, [items.length, itemSelectedIndex]);
    useEffect(() => {
        if (listRef.current) {
            listRef.current.resetAfterIndex(0, true);
        }
    }, [items]);
    return (React.createElement(Box, { sx: (theme) => ({
            padding: theme.spacing(0),
            height: `100%`,
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-container': {
                maxHeight: 'none',
            },
        }) },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(VariableSizeList, { ref: listRef, height: height, width: width, itemSize: getItemSize, overscanCount: 10, initialScrollOffset: itemHeightsSum > height ? itemHeightsSum : 0, itemCount: items.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, itemData: {
                    items,
                    itemSelectedIndex,
                    handleItemClick,
                } }, MapTransportEventListItem)))))),
        items.length === 0 && renderEmpty()));
};
const MapTransportEventListItem = ({ style, index, data }) => {
    const { items, itemSelectedIndex, handleItemClick } = data;
    const item = items[index];
    const isSuccess = item.messages.some((message) => ['guard_out8:on', 'guard_out8:off'].includes(message.key));
    const isAlert = item.messages.some((message) => ['guard_status8:on', 'guard_status8:off'].includes(message.key));
    const isWarning = item.messages.some((message) => ['event_id:1'].includes(message.key));
    const isDanger = item.messages.some((message) => ['event_id:12'].includes(message.key));
    return (React.createElement(TimelineItem, { key: index, style: Object.assign(Object.assign({}, style), { width: 'calc(100% - 24px)', top: `${parseStyleOffset(style.top) + (config.mapEventListOptions.itemPadding - 4)}px`, left: `${parseStyleOffset(style.left) + (config.mapEventListOptions.itemPadding - 4)}px` }), sx: (theme) => (Object.assign({ display: 'flex', minHeight: 'auto', WebkitBoxPack: 'start', position: 'relative', '&:before': {
                display: 'none',
            }, '&:first-of-type': {
                '.MuiTimelineConnector-root': {
                    marginTop: '10px',
                },
            }, '&:last-of-type': {
                '.MuiTimelineConnector-root': {
                    height: '13px',
                    marginBottom: theme.spacing(0),
                },
            }, '.MuiTimelineItemButton-root': Object.assign({ padding: theme.spacing(0), cursor: 'default' }, (item.type === 'message' && {
                cursor: 'pointer',
            })), '.MuiTimelineDot-root': {
                position: 'absolute',
                top: '8.5px',
                left: '4px',
                bgcolor: 'background.paper',
                border: '5px solid',
                borderColor: theme.palette.primary.main,
                margin: theme.spacing(0),
                boxShadow: 'none',
                '&.MuiTimelineDot-filledPrimary': {
                    bgcolor: theme.palette.primary.main,
                },
            }, '.MuiTimelineConnector-root': {
                height: '100%',
                border: '1px dashed',
                bgcolor: 'transparent',
                marginBottom: '2px',
                marginRight: '4px',
                flexGrow: 'unset',
                borderColor: theme.palette.primary.main,
            }, '.MuiTimelineSeparator-root': {
                width: '30px',
                height: '100%',
                flex: 'none',
                position: 'relative',
            }, '.MuiTimelineContent-root': Object.assign(Object.assign(Object.assign(Object.assign({ position: 'relative', bgcolor: 'background.paper', border: '1px solid', borderColor: theme.palette.divider, borderRadius: theme.shape.borderRadius, padding: theme.spacing(2), marginBottom: '8px', '&:before': Object.assign(Object.assign(Object.assign(Object.assign({ top: '16px', width: '10px', left: '-5px', height: '10px', zIndex: '0', content: '""', display: 'block', position: 'absolute', transform: 'translateY(-50%) rotate(45deg)', bgcolor: 'background.paper', borderLeft: '1px solid', borderBottom: '1px solid', borderColor: theme.palette.divider }, (isSuccess && {
                    bgcolor: '#aaffaa',
                })), (isAlert && {
                    bgcolor: '#ffaaaa',
                })), (isWarning && {
                    bgcolor: '#ffc107',
                })), (isDanger && {
                    bgcolor: '#ec7d02',
                })), '.MuiTypography-root': Object.assign({ fontSize: '12px', lineHeight: '16px' }, (isDanger && {
                    color: '#ffffff',
                })), '.MuiTimelineItemDatetime-root': {
                    'h6:last-of-type': Object.assign({ marginLeft: '6px', color: theme.palette.text.secondary }, (isDanger && {
                        color: '#ffffff',
                    })),
                }, 'span.MuiTypography-root': Object.assign({ color: theme.palette.text.secondary }, (isDanger && {
                    color: '#ffffff',
                })) }, (isSuccess && {
                bgcolor: '#aaffaa',
            })), (isAlert && {
                bgcolor: '#ffaaaa',
            })), (isWarning && {
                bgcolor: '#ffc107',
            })), (isDanger && {
                bgcolor: '#ec7d02',
                color: '#ffffff',
            })) }, (itemSelectedIndex !== -1 &&
            itemSelectedIndex === index && {
            '& .MuiTimelineDot-root': {
                bgcolor: theme.palette.primary.main,
            },
        }))) },
        React.createElement(ListItemButton, { disableTouchRipple: true, className: 'MuiTimelineItemButton-root', onClick: () => item.type === 'message' && handleItemClick(item) },
            React.createElement(TimelineSeparator, null,
                React.createElement(TimelineDot, null),
                React.createElement(TimelineConnector, null)),
            React.createElement(TimelineContent, null,
                React.createElement(Stack, { direction: 'column' },
                    React.createElement(Stack, { direction: 'row', justifyContent: 'space-between' },
                        React.createElement(Stack, { direction: 'row', className: 'MuiTimelineItemDatetime-root' },
                            React.createElement(Typography, { variant: 'h6' }, item.type === 'message'
                                ? `${moment(item.datetime).format('DD.MM HH:mm:ss')},`
                                : moment(item.datetime).format('DD.MM HH:mm:ss')),
                            React.createElement(Typography, { variant: 'h6' }, item.type === 'message' && item.receive_date ? moment(item.receive_date).format('DD.MM HH:mm:ss') : '')),
                        React.createElement(Typography, { component: 'span' }, item.data.total_mileage > 0 &&
                            `${Math.round(item.data.total_mileage / 1000).toLocaleString('ru')} ${config.conditions.total_mileage.unit}`)),
                    React.createElement(Stack, { direction: 'row', justifyContent: 'space-between' },
                        React.createElement(Typography, { variant: 'body1' }, item.messages
                            .map((message) => formatEventMessage(message, item))
                            .filter(Boolean)
                            .join(', ')),
                        item.type === 'message' && Number.isInteger(item.data.active_sim) && (React.createElement(Typography, { component: 'span' },
                            "\u0421\u0418\u041C_",
                            Number(item.data.active_sim) + 1))))))));
};
export default MapTransportEventList;
